import { FileSystemAdapter } from '../../src/utils/file-system-adapter.js';
import { loadLlmailConfig } from '../../src/utils/config.js';
import { logger } from '../../src/utils/logger.js';
import { dump } from 'js-yaml';
import path from 'path';

export interface InitOptions {
  configPath?: string;  // Path to config file or directory
}

export interface InitResult {
  success: boolean;
  error?: string;
  configPath?: string;
}

const defaultConfig = {
  version: 2,
  types_list: ['bug', 'feature', 'task'],
  active_states: ['active', 'blocked', 'inprogress'],
  inactive_reasons: ['done', 'wontfix', 'duplicate'],
  inactive_subfolders_for_types: true,
  issues_dir: 'issues',
  inbox_dir: 'issues/inbox',
  require_inactive_reason: true
};

/**
 * Creates the directory structure for a new LLMail repository
 */
async function createDirectories(fs: FileSystemAdapter, config: any): Promise<void> {
  logger.info('Creating directory structure...');

  // Create root and inbox directories
  await fs.ensureDir(config.issues_dir);
  await fs.ensureDir(config.inbox_dir);
  await fs.ensureDir(path.join(config.inbox_dir, '_templates'));

  // Create type directories
  for (const type of config.types_list) {
    const typeDir = path.join(config.issues_dir, type);
    await fs.ensureDir(typeDir);

    // Create inactive reason subdirectories if enabled
    if (config.inactive_subfolders_for_types) {
      for (const reason of config.inactive_reasons) {
        await fs.ensureDir(path.join(typeDir, reason));
      }
    }
  }

  // Create active state directories
  for (const state of config.active_states) {
    if (state !== 'inbox') { // inbox already created
      await fs.ensureDir(path.join(config.issues_dir, `_${state}`));
    }
  }
}

/**
 * Initialize a new LLMail repository
 */
export async function init(fs: FileSystemAdapter, options: InitOptions = {}): Promise<InitResult> {
  logger.info('Starting initialization...');
  
  try {
    // Determine config path
    const configDir = options.configPath || process.cwd();
    const configPath = path.join(configDir, 'llmail.yaml');

    // Write default config if it doesn't exist
    if (!(await fs.exists(configPath))) {
      logger.info('Creating default config file...');
      await fs.ensureDir(configDir);
      await fs.writeFile(configPath, dump(defaultConfig));
    }

    // Load config (either default or existing)
    const config = await loadLlmailConfig(fs, configDir);

    // Create directory structure
    await createDirectories(fs, config);

    logger.success('Initialization completed successfully');
    return {
      success: true,
      configPath
    };
  } catch (err: any) {
    logger.error(`Initialization failed: ${err.message}`);
    return {
      success: false,
      error: err.message
    };
  }
} 